import { SalesforceActions } from 'module/api/integrations/salesforce/types';
import { Breadcrumb } from 'module/ui/components/atoms/breadcrumb';
import { ButtonGroup } from 'module/ui/components/atoms/button';
import { CenterAlignedBox } from 'module/ui/components/atoms/layout/layout';
import { Page } from 'module/ui/components/molecules/page';
import { showToast, ToastType } from 'module/ui/components/molecules/toasts';
import { Argon } from 'module/ui/components/templates/argon';
import { Button, Loader } from '@rocketlane/ui';
import { useCreateAutomationMutation } from 'queries/salesforce';
import { useCallback, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Link, useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { transformSfToApiFormat } from 'components/organisms/integrations/salesforce/utils/transformSfToApiFormat';
import { getSalesforceAutomationUrl } from 'components/pages/integrations/salesforce/utils/urls';
import { useAutomationContext } from './automation-context';
import { CreateAutomation } from './create';

const {
  Header: { Title },
} = Page;
const PageActionWrapper = styled(CenterAlignedBox)``;

const requiredFields = [
  'automationName',
  'salesforceObject',
  'triggers',
  'accountFields',
];

function CreateAutomationLayout() {
  const { automationDetails, isContactSelected, isContactSyncConfigured } =
    useAutomationContext();
  const { integrationId } = useParams<{ integrationId: string }>();
  const { mutate: createAutomationMutation, status: creationStatus } =
    useCreateAutomationMutation();
  const navigate = useNavigate();
  const [isSaveButtonLoading, setIsSaveButtonLoading] = useState(false);

  const canEnableSaveButton = useMemo(() => {
    const clonedRequiredFields = [...requiredFields];

    if (automationDetails?.salesForceType === SalesforceActions.Project) {
      clonedRequiredFields.push('rocketlaneFields');
    }

    // Check basic required fields
    const isBasicValid = clonedRequiredFields.every(
      (field) => automationDetails?.[field],
    );

    // If contact toggle is OFF, only check basic fields
    if (!isContactSelected) {
      return isBasicValid;
    }

    // Contact toggle is ON - validate contact configuration
    const hasContactObject = !!automationDetails?.sourceResourceType;
    const hasContactFields =
      automationDetails?.contactFields &&
      Object.keys(automationDetails.contactFields).length > 0;

    // All conditions must be met:
    // 1. Basic fields valid
    // 2. Contact object selected
    // 3. Contact fields filled
    // 4. Mandatory fields sync configured
    return (
      isBasicValid &&
      hasContactObject &&
      hasContactFields &&
      isContactSyncConfigured
    );
  }, [automationDetails, isContactSelected, isContactSyncConfigured]);

  const handleSave = useCallback(() => {
    if (automationDetails && integrationId) {
      setIsSaveButtonLoading(true);
      const payload = transformSfToApiFormat(
        parseInt(integrationId),
        automationDetails,
      );

      if (payload) {
        createAutomationMutation(payload, {
          onSuccess: () => {
            navigate(getSalesforceAutomationUrl(integrationId), {
              replace: true,
            });
          },
          onError: (error) => {
            setIsSaveButtonLoading(false);
            showToast({
              toastContainer: { current: document.body },
              content: error.message,
              variant: ToastType.Error,
            });
          },
        });
      } else {
        setIsSaveButtonLoading(false);
      }
    }
  }, [automationDetails, integrationId, createAutomationMutation, navigate]);

  const handleCancel = useCallback(() => {
    // Check if user has filled any fields
    const hasUnsavedChanges =
      automationDetails &&
      (automationDetails.automationName ||
        automationDetails.salesforceObject ||
        automationDetails.triggers ||
        automationDetails.matches ||
        automationDetails.rocketlaneFields ||
        automationDetails.accountFields ||
        automationDetails.contactFields);

    if (hasUnsavedChanges) {
      // Show confirmation dialog
      const confirmLeave = window.confirm(
        'You have unsaved changes. Are you sure you want to cancel?',
      );

      if (!confirmLeave) {
        return; // Stay on page
      }
    }

    // Navigate back to automations list
    navigate(getSalesforceAutomationUrl(integrationId || ''));
  }, [automationDetails, integrationId, navigate]);

  return (
    <Argon
      breadcrumbs={
        <Breadcrumb.Item>
          <Link to={getSalesforceAutomationUrl(integrationId || '')}>
            <FormattedMessage id="salesforce" />
          </Link>
          <Breadcrumb.Separator />
        </Breadcrumb.Item>
      }
      pageActions={
        <PageActionWrapper>
          <ButtonGroup $reversed>
            <Button
              variant="primary"
              disabled={!canEnableSaveButton || isSaveButtonLoading}
              onClick={handleSave}
              loading={isSaveButtonLoading || creationStatus === 'pending'}
            >
              {isSaveButtonLoading || creationStatus === 'pending' ? (
                <Loader />
              ) : (
                <FormattedMessage id="save" />
              )}
            </Button>
            <Button variant="secondary" onClick={handleCancel}>
              <FormattedMessage id="cancel" />
            </Button>
          </ButtonGroup>
        </PageActionWrapper>
      }
      title={<Title>{'New automation to create a Scarlet account'}</Title>}
      documentTitle={'New automation to create a Scarlet account'}
      actions={{}}
      content={
        <>
          <CreateAutomation />
        </>
      }
    />
  );
}

export { CreateAutomationLayout };
